import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { formsApi } from '../../services/api';
import {
    ClipboardList,
    Plus,
    Pencil,
    Trash2,
    X,
    GripVertical,
    Star,
    MessageSquare,
    ListChecks,
    Camera,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    ChevronsUp,
    ChevronsDown,
    Copy,
    Eye
} from 'lucide-react';

const QUESTION_TYPES = {
    1: { label: 'Açık Metin', icon: MessageSquare },
    2: { label: 'Yıldız Puanlama (1-5)', icon: Star },
    3: { label: 'Çoktan Seçmeli', icon: ListChecks }
};

const PHOTO_REQUIREMENTS = {
    1: { label: 'Devre Dışı', color: 'gray' },
    2: { label: 'İsteğe Bağlı', color: 'blue' },
    3: { label: 'Zorunlu', color: 'red' }
};

const FormTemplatesPage = () => {
    const { selectedCorporate } = useOutletContext();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expandedTemplate, setExpandedTemplate] = useState(null);

    const [formData, setFormData] = useState({
        corporateId: '',
        title: '',
        isPersonelLoginRequired: false,
        questions: [],
        status: 1
    });

    useEffect(() => {
        if (selectedCorporate) {
            loadData();
        }
    }, [selectedCorporate]);

    const loadData = async () => {
        if (!selectedCorporate?.id) return;
        setLoading(true);
        setError(null);
        try {
            const templatesData = await formsApi.getTemplates(selectedCorporate?.id);
            setTemplates(templatesData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingTemplate(null);
        setFormData({
            corporateId: selectedCorporate?.id || '',
            title: '',
            description: '',
            isPersonelLoginRequired: false,
            questions: [{
                questionText: '',
                questionType: 1,
                isRequired: false,
                photoRequirement: 1,
                orderIndex: 0,
                options: []
            }],
            status: 1
        });
        setShowModal(true);
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        setFormData({
            corporateId: template.corporateId || selectedCorporate?.id || '',
            title: template.title,
            description: template.description || '',
            isPersonelLoginRequired: template.isPersonelLoginRequired || false,
            questions: template.questions?.map((q, idx) => ({
                id: q.id,
                questionText: q.questionText,
                questionType: q.questionType,
                isRequired: q.isRequired,
                photoRequirement: q.photoRequirement,
                orderIndex: idx,
                options: q.options?.map(opt => typeof opt === 'string' ? opt : opt.optionText) || []
            })) || [],
            status: template.status !== undefined && template.status !== null ? template.status : 1
        });
        setShowModal(true);
    };

    const handleAddQuestion = () => {
        const newQuestion = {
            questionText: '',
            questionType: 1,
            isRequired: false,
            photoRequirement: 1,
            orderIndex: formData.questions.length,
            options: []
        };
        setFormData({
            ...formData,
            questions: [...formData.questions, newQuestion]
        });
    };

    const handleUpdateQuestion = (index, field, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[index] = {
            ...updatedQuestions[index],
            [field]: value
        };
        // Reset options if question type changes from MultipleChoice
        if (field === 'questionType') {
            if (value !== 3) {
                updatedQuestions[index].options = [];
            } else if (updatedQuestions[index].options.length === 0) {
                updatedQuestions[index].options = ['', ''];
            }
        }
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleRemoveQuestion = (index) => {
        const updatedQuestions = formData.questions.filter((_, i) => i !== index);
        // Update order indices
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleMoveQuestion = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= formData.questions.length) return;

        const updatedQuestions = [...formData.questions];
        const temp = updatedQuestions[index];
        updatedQuestions[index] = updatedQuestions[newIndex];
        updatedQuestions[newIndex] = temp;

        // Update order indices
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleMoveToExtreme = (index, target) => {
        if (target === 'top' && index === 0) return;
        if (target === 'bottom' && index === formData.questions.length - 1) return;

        const updatedQuestions = [...formData.questions];
        const [removed] = updatedQuestions.splice(index, 1);
        if (target === 'top') {
            updatedQuestions.unshift(removed);
        } else {
            updatedQuestions.push(removed);
        }
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleDuplicateQuestion = (index) => {
        const updatedQuestions = [...formData.questions];
        const original = updatedQuestions[index];
        const duplicate = {
            ...original,
            id: undefined,
            options: [...(original.options || [])]
        };
        updatedQuestions.splice(index + 1, 0, duplicate);
        updatedQuestions.forEach((q, i) => q.orderIndex = i);
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleAddOption = (questionIndex) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[questionIndex].options.push('');
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleUpdateOption = (questionIndex, optionIndex, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[questionIndex].options[optionIndex] = value;
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleRemoveOption = (questionIndex, optionIndex) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, i) => i !== optionIndex);
        setFormData({ ...formData, questions: updatedQuestions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.questions.length === 0) {
            alert('En az bir soru eklemelisiniz.');
            return;
        }

        setSaving(true);
        try {
            if (editingTemplate) {
                await formsApi.updateTemplate(editingTemplate.id, formData);
            } else {
                await formsApi.createTemplate(formData);
            }
            setShowModal(false);
            await loadData();
        } catch (err) {
            alert('Kaydetme işlemi başarısız: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (template) => {
        if (window.confirm(`"${template.title}" form şablonunu silmek istediğinize emin misiniz?`)) {
            try {
                await formsApi.deleteTemplate(template.id);
                await loadData();
            } catch (err) {
                alert('Silme işlemi başarısız: ' + err.message);
            }
        }
    };

    const getQuestionTypeIcon = (type) => {
        const Icon = QUESTION_TYPES[type]?.icon || MessageSquare;
        return <Icon className="w-4 h-4" />;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-6">
                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Form Şablonları</h1>
                        <p className="text-gray-500 mt-1">Form şablonlarını yönetin</p>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        Yeni Form Şablonu
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <p className="text-red-700">{error}</p>
                    </div>
                )}

                {/* Templates List */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    {templates.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p>Form şablonu bulunamadı.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {templates.map((template) => (
                                <div key={template.id} className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                <ClipboardList className="w-5 h-5 text-purple-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-gray-900">{template.title}</h3>
                                                <p className="text-sm text-gray-500">
                                                    {template.questions?.length || 0} soru
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${template.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                }`}>
                                                {template.status === 1 ? 'Aktif' : 'Pasif'}
                                            </span>
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${template.isPersonelLoginRequired ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                Personel Giriş: {template.isPersonelLoginRequired ? 'Gerekli' : 'Gerekli Değil'}
                                            </span>
                                            <button
                                                onClick={() => handleEdit(template)}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                title="Düzenle"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                title="Detayları Gör"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Expanded Questions View */}
                                    {expandedTemplate === template.id && template.questions && (
                                        <div className="mt-4 pl-13 space-y-2">
                                            {template.questions.map((question, idx) => (
                                                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                                    <span className="text-sm font-medium text-gray-500">{idx + 1}.</span>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            {getQuestionTypeIcon(question.questionType)}
                                                            <span className="text-sm font-medium text-gray-900">
                                                                {question.questionText}
                                                            </span>
                                                            {question.isRequired && (
                                                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                                                    Zorunlu
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                                            <span>{QUESTION_TYPES[question.questionType]?.label}</span>
                                                            {question.photoRequirement > 1 && (
                                                                <span className="flex items-center gap-1">
                                                                    <Camera className="w-3 h-3" />
                                                                    {PHOTO_REQUIREMENTS[question.photoRequirement]?.label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {question.options?.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                {question.options.map((opt, optIdx) => (
                                                                    <span key={optIdx} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                                                                        {typeof opt === 'string' ? opt : opt.optionText}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Template Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {editingTemplate ? 'Form Şablonunu Düzenle' : 'Yeni Form Şablonu'}
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Basic Info */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Form Başlığı *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Örn: Temizlik Değerlendirme Formu"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Açıklama
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                                    placeholder="Form açıklamasını girin..."
                                    rows={3}
                                />
                            </div>

                            {/* Form Settings */}
                            <div className="flex flex-wrap gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.isPersonelLoginRequired}
                                        onChange={(e) => setFormData({ ...formData, isPersonelLoginRequired: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700">Personel Giriş Gerekli</span>
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Durum
                                </label>
                                <div className="flex gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 1}
                                            onChange={() => setFormData({ ...formData, status: 1 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Aktif</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={formData.status === 0}
                                            onChange={() => setFormData({ ...formData, status: 0 })}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">Pasif</span>
                                    </label>
                                </div>
                            </div>

                            {/* Questions Section */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-md font-semibold text-gray-900">Sorular</h4>
                                    <button
                                        type="button"
                                        onClick={handleAddQuestion}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Soru Ekle
                                    </button>
                                </div>

                                {formData.questions.length === 0 ? (
                                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                        <MessageSquare className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                                        <p className="text-gray-500">Henüz soru eklenmedi</p>
                                        <button
                                            type="button"
                                            onClick={handleAddQuestion}
                                            className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                        >
                                            İlk soruyu ekle
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {formData.questions.map((question, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex flex-col gap-1 pt-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveToExtreme(index, 'top')}
                                                            disabled={index === 0}
                                                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                                            title="En üste taşı"
                                                        >
                                                            <ChevronsUp className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveQuestion(index, 'up')}
                                                            disabled={index === 0}
                                                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                                            title="Yukarı taşı"
                                                        >
                                                            <ChevronUp className="w-4 h-4" />
                                                        </button>
                                                        <div className="p-1">
                                                            <GripVertical className="w-4 h-4 text-gray-400" />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveQuestion(index, 'down')}
                                                            disabled={index === formData.questions.length - 1}
                                                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                                            title="Aşağı taşı"
                                                        >
                                                            <ChevronDown className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveToExtreme(index, 'bottom')}
                                                            disabled={index === formData.questions.length - 1}
                                                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                                            title="En alta taşı"
                                                        >
                                                            <ChevronsDown className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    <div className="flex-1 space-y-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                                                            <input
                                                                type="text"
                                                                value={question.questionText}
                                                                onChange={(e) => handleUpdateQuestion(index, 'questionText', e.target.value)}
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                placeholder="Soru metnini girin..."
                                                                required
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDuplicateQuestion(index)}
                                                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                                title="Soruyu kopyala"
                                                            >
                                                                <Copy className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveQuestion(index)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                                title="Soruyu sil"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                    Soru Tipi
                                                                </label>
                                                                <select
                                                                    value={question.questionType}
                                                                    onChange={(e) => handleUpdateQuestion(index, 'questionType', parseInt(e.target.value))}
                                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                >
                                                                    {Object.entries(QUESTION_TYPES).map(([value, { label }]) => (
                                                                        <option key={value} value={value}>{label}</option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                    Fotoğraf
                                                                </label>
                                                                <select
                                                                    value={question.photoRequirement}
                                                                    onChange={(e) => handleUpdateQuestion(index, 'photoRequirement', parseInt(e.target.value))}
                                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                >
                                                                    {Object.entries(PHOTO_REQUIREMENTS).map(([value, { label }]) => (
                                                                        <option key={value} value={value}>{label}</option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            <div className="flex items-end">
                                                                <label className="flex items-center gap-2 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={question.isRequired}
                                                                        onChange={(e) => handleUpdateQuestion(index, 'isRequired', e.target.checked)}
                                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                                    />
                                                                    <span className="text-sm text-gray-700">Zorunlu</span>
                                                                </label>
                                                            </div>
                                                        </div>

                                                        {/* Options for Multiple Choice */}
                                                        {question.questionType === 3 && (
                                                            <div className="mt-3 pl-4 border-l-2 border-blue-200">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <label className="text-xs font-medium text-gray-600">Seçenekler</label>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddOption(index)}
                                                                        className="text-xs text-blue-600 hover:text-blue-700"
                                                                    >
                                                                        + Seçenek Ekle
                                                                    </button>
                                                                </div>
                                                                {question.options.length === 0 ? (
                                                                    <p className="text-xs text-gray-400">Henüz seçenek eklenmedi</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {question.options.map((option, optIndex) => (
                                                                            <div key={optIndex} className="flex items-center gap-2">
                                                                                <input
                                                                                    type="text"
                                                                                    value={option}
                                                                                    onChange={(e) => handleUpdateOption(index, optIndex, e.target.value)}
                                                                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                                    placeholder={`Seçenek ${optIndex + 1}`}
                                                                                    required
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleRemoveOption(index, optIndex)}
                                                                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                                                >
                                                                                    <X className="w-4 h-4" />
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </form>

                        <div className="flex gap-3 px-6 py-4 border-t bg-gray-50">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {saving ? 'Kaydediliyor...' : (editingTemplate ? 'Güncelle' : 'Kaydet')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormTemplatesPage;
